import { USER_ERROR_CODES } from "@lib/constants/error-codes";
import { QueryKeys } from "@lib/enums/QueryKeys";
import { FetchServiceAxios } from "@lib/helpers/fetchServiceAxios";
import type { QueryConfig } from "@lib/helpers/useApi";
import { useApi } from "@lib/helpers/useApi";
import { withErrorHandler } from "@lib/utils/promiseWithErrorHandler";
import type { Customer } from "@store/types";
import { useSession } from "next-auth/react";
import useSignOut from "./useSignOut";

const getUser = async (signOut: () => void) =>
    withErrorHandler(
        () => FetchServiceAxios.get<Customer>(`/api/user/get-user`),
        [{ errorCode: USER_ERROR_CODES.CUSTOMER_GET_FAILURE, callback: signOut }]
    );

type UseProfileParams<TData = Customer> = {
    // Allow extending queryConfig, including select
    queryConfig?: QueryConfig<Customer, TData>;
};

export const useProfile = <TData = Customer>(params?: UseProfileParams<TData>) => {
    const { queryConfig } = params ?? {};
    const { data: session, status } = useSession();
    const { signOut } = useSignOut();

    const queryConfigWithDefaults = {
        enabled: status === "authenticated",
        ...(queryConfig ?? {}),
    };

    return useApi.useAsync<Customer, TData>(
        [QueryKeys.getProfile, session?.anonymousId, session?.ucid],
        async () => {
            if (!session?.ucid) {
                return {} as Customer;
            }
            return await getUser(signOut);
        },
        queryConfigWithDefaults
    );
};
